/**
 * Client TalentCompetency (Sprint TCO-B).
 *
 * Closes the operational loop with the CTH team. Client Admin can:
 *   • Case A — request an evaluation for an employee using a PUBLISHED
 *     competency profile.
 *   • Case B — request a NEW profile (title + department + description +
 *     business justification). HM Advisor will build it via the TCO-A
 *     catalog, get it published, then return to the request to assign.
 *
 * No new questionnaire / assignment engine. The Assign step on the HM side
 * fans out through the existing assignment engine for talentcompetency.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Target, Plus, FilePlus, Eye, X, ClipboardList, Sparkles, FileText, UserRound,
  BookOpenCheck, GraduationCap, ArrowRight, Loader2, Link2, LifeBuoy,
} from 'lucide-react';
import { DonutCard, BarsCard, SEM } from '@/components/client/SuiteAnalytics';
import { CompetencyCoverageHeatmap } from '@/components/client/CompetencyCoverageHeatmap';
import { PendingGapsCard } from '@/components/client/PendingGapsCard';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronsUpDown, Check } from 'lucide-react';

import { Topbar } from '@/components/layout/Topbar';
import { SuiteHero } from '@/components/client/SuiteHero';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';

import { competencyRequestClientApi } from '@/lib/competencyProfileApi';
import { api, formatApiError } from '@/lib/api';
import { isActiveCollaborator } from '@/lib/employment';
import { getUiLocale } from '@/lib/dates';

const STATUS_TONES = {
  pending:   'bg-amber-50 text-amber-700 border-amber-200',
  profile_available: 'bg-sky-50 text-sky-700 border-sky-200',
  assigned:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected:  'bg-rose-50 text-rose-700 border-rose-200',
  cancelled: 'bg-zinc-100 text-zinc-500 border-zinc-200',
};

// TC-Closeout FIX 6/16 — downstream execution lifecycle (read-only, derived).
const EXECUTION_STAGES = [
  'assigned', 'in_progress', 'evaluation_completed',
  'report_generating', 'report_in_review', 'report_published',
];
const STAGE_TONES = {
  assigned:             'bg-zinc-100 text-zinc-600 border-zinc-200',
  in_progress:          'bg-amber-50 text-amber-700 border-amber-200',
  evaluation_completed: 'bg-sky-50 text-sky-700 border-sky-200',
  report_generating:    'bg-indigo-50 text-indigo-700 border-indigo-200',
  report_in_review:     'bg-violet-50 text-violet-700 border-violet-200',
  report_published:     'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function ClientTalentCompetency() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [caseAOpen, setCaseAOpen] = useState(false);
  const [caseBOpen, setCaseBOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [caseADraft, setCaseADraft] = useState({
    employee_user_id: '', competency_profile_id: '', notes: '',
  });
  const [caseBDraft, setCaseBDraft] = useState({
    proposed_title: '', proposed_department: '', proposed_description: '',
    business_justification: '', employee_user_id: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Founder UAT 2026-06 — TalentCompetency separates two workflows:
  // 'knowledge' (direct knowledge assessment, NO position required) and
  // 'position' (position-linked, uses configured competency profiles).
  const [purpose, setPurpose] = useState('knowledge');
  const [knowledge, setKnowledge] = useState([]);
  const [libAssessments, setLibAssessments] = useState([]);
  const [knowledgeDraft, setKnowledgeDraft] = useState({ questionnaire_id: '', notes: '' });
  const [handoffCtx, setHandoffCtx] = useState(null); // {trainingTitle, trainingId}
  const [compLinks, setCompLinks] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [companyComps, setCompanyComps] = useState([]);
  const [roleProfiles, setRoleProfiles] = useState([]);
  const [assessOpen, setAssessOpen] = useState(false);
  // Founder UAT remediation 2026-06 — canonical position auto-resolution:
  // collaborator → formal position → Role Profile → competencies → instruments.
  const [resolution, setResolution] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState([]);
  useEffect(() => {
    if (!caseAOpen || purpose !== 'position' || !caseADraft.employee_user_id) {
      setResolution(null); setSelectedCodes([]);
      return undefined;
    }
    let alive = true;
    setResolving(true);
    api.get(`/client/talent-competency/position-resolution/${caseADraft.employee_user_id}`)
      .then((r) => { if (alive) { setResolution(r.data); setSelectedCodes([]); } })
      .catch(() => { if (alive) setResolution(null); })
      .finally(() => { if (alive) setResolving(false); });
    return () => { alive = false; };
  }, [caseAOpen, purpose, caseADraft.employee_user_id]);
  // Founder UAT 2026-06 — clean, deduplicated, alphabetical selector options.
  const selectorOptions = useMemo(() => {
    const seen = new Set();
    return (libAssessments || [])
      .map((qq) => ({
        id: qq.id,
        label: (i18n.language?.startsWith('es') && qq.name_es) || qq.name || qq.title || '',
        area: qq.professional_area || null,
        passing: qq.passing_score ?? null,
      }))
      .filter((o) => {
        if (!o.label || seen.has(o.id) || seen.has(o.label.toLowerCase())) return false;
        seen.add(o.id); seen.add(o.label.toLowerCase());
        return true;
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [libAssessments, i18n.language]);
  // Founder P1 2026-09 — TalentTrain → TalentCompetency handoff:
  // /client/talentcompetency?evaluate=<uid>&fromTraining=<name>&trainingId=<id>
  // opens the dialog pre-selecting the employee with knowledge purpose.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const uid = searchParams.get('evaluate');
    const preQ = searchParams.get('knowledgeAssessment');
    if (uid || preQ) {
      const trainingTitle = searchParams.get('fromTraining') || null;
      const trainingId = searchParams.get('trainingId') || null;
      if (uid) setCaseADraft((d) => ({ ...d, employee_user_id: uid }));
      if (trainingTitle) {
        setPurpose('knowledge');
        setHandoffCtx({ trainingTitle, trainingId });
      }
      // Founder-approved: one-click assignment from a linked competency.
      if (preQ) {
        setPurpose('knowledge');
        setKnowledgeDraft((d) => ({ ...d, questionnaire_id: preQ }));
      }
      setCaseAOpen(true);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const [rs, ps, us, ka, lib, ln, cat, cc, rp] = await Promise.all([
        competencyRequestClientApi.list(),
        competencyRequestClientApi.listPublishedProfiles(),
        api.get('/client/users', { params: { population: 'active_collaborators' } }).then((r) => r.data),
        api.get('/client/talent-competency/knowledge-assessments').then((r) => r.data).catch(() => ({ items: [] })),
        api.get('/client/library').then((r) => r.data).catch(() => []),
        api.get('/client/talent-competency/competency-links').then((r) => r.data).catch(() => ({})),
        api.get('/admin/rb/competencies').then((r) => (Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => []),
        api.get('/client/talent-competency/company-competencies').then((r) => r.data).catch(() => []),
        api.get('/client/role-profiles').then((r) => r.data).catch(() => []),
      ]);
      setRequests(rs);
      setProfiles(ps);
      setKnowledge(ka?.items || []);
      setLibAssessments(Array.isArray(lib) ? lib : (lib?.items || []));
      setCompLinks(ln || {});
      setCatalog(cat || []);
      setCompanyComps(cc || []);
      setRoleProfiles(rp || []);
      // Founder P0 2026-09 — one authoritative rule: active collaborators
      // regardless of extra platform permissions (client_admin included).
      setEmployees((us || []).filter(isActiveCollaborator));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const detail = useMemo(
    () => requests.find((r) => r.id === detailId) || null,
    [requests, detailId],
  );

  // TC-Closeout FIX 16 — flatten every downstream evaluation so the client
  // sees WHO is being evaluated, against WHICH profile, and the report state.
  const evaluations = useMemo(() => requests.flatMap((r) => (
    (r.execution?.assignments || []).map((a) => ({
      ...a,
      requestId: r.id,
      profileId: r.assigned_profile_id || r.competency_profile_id,
      employeeUserId: r.employee_user_id,
    }))
  )), [requests]);

  const employeeName = (uid) => {
    const e = employees.find((x) => x.id === uid);
    return e ? e.name : uid || '—';
  };
  const profileName = (pid) => {
    const p = profiles.find((x) => x.id === pid);
    return p ? p.name : pid || '—';
  };

  // Founder-approved: reminders (existing email architecture, 48h throttle)
  // and audited PDF/Excel exports of knowledge results.
  const [remindBusy, setRemindBusy] = useState(null);
  const sendReminder = async (aid) => {
    setRemindBusy(aid);
    try {
      const { data } = await api.post(`/client/talent-competency/knowledge-assessments/${aid}/remind`);
      toast.success(t('tco.knowledge.reminderSent', { defaultValue: 'Recordatorio enviado' }), {
        description: data.sent_to,
      });
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 429 && det?.code === 'reminder_throttled') {
        toast.info(t('tco.knowledge.reminderThrottled', {
          defaultValue: 'Ya se envió un recordatorio recientemente. Próximo disponible: {{at}}',
          at: new Date(det.next_allowed_at).toLocaleString(getUiLocale()),
        }));
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setRemindBusy(null); }
  };
  const exportKnowledge = async (fmt) => {
    try {
      const res = await api.get(`/client/talent-competency/knowledge-assessments/export/${fmt}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluaciones_conocimientos.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitKnowledge = async () => {
    if (!caseADraft.employee_user_id) return toast.error(t('tco.client.errors.employee', { defaultValue: 'Pick an employee.' }));
    if (!knowledgeDraft.questionnaire_id) return toast.error(t('tco.client.errors.assessment', { defaultValue: 'Selecciona una evaluación de la biblioteca.' }));
    setSubmitting(true);
    try {
      const { data } = await api.post('/client/talent-competency/knowledge-assessments', {
        employee_user_id: caseADraft.employee_user_id,
        questionnaire_id: knowledgeDraft.questionnaire_id,
        notes: knowledgeDraft.notes || null,
        origin: handoffCtx ? 'talenttrain' : 'direct',
        training_action_id: handoffCtx?.trainingId || null,
        training_title: handoffCtx?.trainingTitle || null,
      });
      toast.success(t('tco.client.knowledgeAssigned', { defaultValue: 'Evaluación de conocimientos asignada' }), {
        description: t('tco.client.knowledgeAssignedDesc', {
          defaultValue: 'Ya está disponible en el Mi Portal de {{name}}.',
          name: data.employee_name,
        }),
      });
      closeCaseA();
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const closeCaseA = () => {
    setCaseAOpen(false);
    setCaseADraft({ employee_user_id: '', competency_profile_id: '', notes: '' });
    setKnowledgeDraft({ questionnaire_id: '', notes: '' });
    setHandoffCtx(null);
    setPurpose('knowledge');
  };

  // Founder UAT remediation 2026-06 — assign against the auto-resolved
  // canonical position (selected competencies → linked instruments).
  const submitPositionResolved = async () => {
    if (!caseADraft.employee_user_id) return toast.error(t('tco.client.errors.employee', { defaultValue: 'Pick an employee.' }));
    if (!selectedCodes.length) return toast.error(t('tco.client.errors.competencies', { defaultValue: 'Selecciona al menos una competencia a evaluar.' }));
    setSubmitting(true);
    try {
      const { data } = await api.post('/client/talent-competency/position-evaluations', {
        employee_user_id: caseADraft.employee_user_id,
        competency_codes: selectedCodes,
        notes: caseADraft.notes || null,
      });
      toast.success(t('tco.client.positionEvalAssigned', { defaultValue: 'Evaluación vinculada al puesto asignada' }), {
        description: `${data.employee_name} · ${data.position} · ${data.assignments} instrumento(s)`,
      });
      closeCaseA();
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Founder-approved: request CTH support/publication straight from the
  // diagnostic state (pre-filled Case B — CTH governance preserved).
  const openPublicationRequest = () => {
    const posTitle = resolution?.position?.title || '';
    const missing = (resolution?.competencies || []).filter((c) => !c.has_instrument).map((c) => c.name);
    setCaseBDraft({
      proposed_title: posTitle,
      proposed_department: resolution?.role_profile?.department || '',
      proposed_description: '',
      business_justification: t('tco.client.publicationJustification', {
        defaultValue: 'Solicitud de publicación de instrumentos de evaluación para el puesto "{{pos}}". Competencias configuradas sin instrumento publicado: {{comps}}.',
        pos: posTitle || '—',
        comps: missing.length ? missing.join(', ') : '—',
      }),
      employee_user_id: caseADraft.employee_user_id || '',
    });
    setCaseAOpen(false);
    setCaseBOpen(true);
  };

  const submitCaseA = async () => {
    if (!caseADraft.employee_user_id) return toast.error(t('tco.client.errors.employee', { defaultValue: 'Pick an employee.' }));
    if (!caseADraft.competency_profile_id) return toast.error(t('tco.client.errors.profile', { defaultValue: 'Pick a competency profile.' }));
    setSubmitting(true);
    try {
      // Founder UAT TC-013/014 — target position is derived from the selected
      // profile (snapshot preserved in the data model), never free-typed.
      await competencyRequestClientApi.selfAssign({
        employee_user_id: caseADraft.employee_user_id,
        competency_profile_id: caseADraft.competency_profile_id,
        notes: caseADraft.notes || null,
      });
      toast.success(t('tco.client.assignedDirect', { defaultValue: 'Evaluación asignada al colaborador' }));
      setCaseAOpen(false);
      setCaseADraft({ employee_user_id: '', competency_profile_id: '', notes: '' });
      reload();
    } catch (e) {
      // Founder UAT TC-016/017 — business-oriented bilingual message.
      if (e?.response?.data?.detail === 'profile_has_no_linked_questionnaires') {
        toast.error(t('tco.client.errors.profileNoAssessments', {
          defaultValue: 'This competency profile has not yet been linked to any published assessments. Corporate Talent Hub must associate at least one assessment before this evaluation can be requested.',
        }));
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitCaseB = async () => {
    if (!caseBDraft.proposed_title.trim()) return toast.error(t('tco.client.errors.title', { defaultValue: 'Position title is required.' }));
    if (!caseBDraft.business_justification.trim()) return toast.error(t('tco.client.errors.justification', { defaultValue: 'Business justification is required.' }));
    setSubmitting(true);
    try {
      await competencyRequestClientApi.create({
        type: 'new_profile',
        proposed_title: caseBDraft.proposed_title.trim(),
        proposed_department: caseBDraft.proposed_department.trim() || null,
        proposed_description: caseBDraft.proposed_description.trim() || null,
        business_justification: caseBDraft.business_justification.trim(),
        employee_user_id: caseBDraft.employee_user_id || null,
      });
      toast.success(t('tco.client.requestSent', { defaultValue: 'Request sent to CTH' }));
      setCaseBOpen(false);
      setCaseBDraft({ proposed_title: '', proposed_department: '', proposed_description: '', business_justification: '', employee_user_id: '' });
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelRequest = async () => {
    if (!detail) return;
    setCancelling(true);
    try {
      await competencyRequestClientApi.cancel(detail.id);
      toast.success(t('tco.client.cancelled', { defaultValue: 'Request cancelled' }));
      setDetailId(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <Topbar
        title={(
          <span className="inline-flex items-center gap-2">
            TalentCompetency
            <Badge className="bg-primary/15 text-primary border-primary/30">{t('suiteChip.talentcompetency')}</Badge>
          </span>
        )}
        subtitle={t('tco.client.subtitle2', { defaultValue: 'Evaluación de Competencias por Puesto' })}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/client/talentcompetency/competencias')} data-testid="tco-client-configure-btn">
              <Target className="h-4 w-4 mr-1.5" />
              {t('tco.client.configureCompetencies', { defaultValue: 'Configurar competencias' })}
            </Button>
            <Button variant="outline" onClick={() => setCaseBOpen(true)} data-testid="tco-client-new-profile-btn">
              <FilePlus className="h-4 w-4 mr-1.5" />
              {t('tco.client.requestNewProfile', { defaultValue: 'Solicitar apoyo de CTH' })}
            </Button>
            <Button onClick={() => setCaseAOpen(true)} data-testid="tco-client-new-evaluation-btn">
              <Plus className="h-4 w-4 mr-1.5" />
              {t('tco.client.assignEvaluation', { defaultValue: 'Evaluar colaborador' })}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="tco-client-page">
        {/* Suite Unification — persistent hero (hidden when the onboarding hero shows) */}
        {!loading && !(requests.length === 0 && profiles.length === 0) && (
          <SuiteHero suite="talentcompetency" icon={Target} />
        )}
        {/* Founder UX Review — onboarding hero when the suite is empty */}
        {!loading && requests.length === 0 && profiles.length === 0 && (
          <Card className="p-8 shadow-card border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="tco-client-empty-hero">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">{t('tco.client.hero.title', { defaultValue: 'Bienvenido a TalentCompetency' })}</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                {t('tco.client.hero.body', { defaultValue: 'TalentCompetency evalúa a tus colaboradores contra las competencias que cada puesto exige. Descubre quién domina su rol, dónde hay brechas y qué desarrollar — con perfiles de puesto diseñados por Corporate Talent Hub.' })}
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                {[
                  { n: 1, title: t('tco.client.hero.step1', { defaultValue: 'Configura las competencias del puesto' }), body: t('tco.client.hero.step1Body', { defaultValue: 'Define las competencias requeridas de cada puesto directamente, o solicita apoyo de CTH si lo prefieres.' }) },
                  { n: 2, title: t('tco.client.hero.step2', { defaultValue: 'Evalúa a tus colaboradores' }), body: t('tco.client.hero.step2Body', { defaultValue: 'Asigna evaluaciones publicadas a los colaboradores para medirlos contra las competencias de su puesto.' }) },
                  { n: 3, title: t('tco.client.hero.step3', { defaultValue: 'Analiza brechas y desarrolla' }), body: t('tco.client.hero.step3Body', { defaultValue: 'Revisa resultados y brechas por colaborador y conéctalas con acciones de desarrollo en TalentTrain.' }) },
                ].map((s) => (
                  <div key={s.n} className="rounded-lg border border-border bg-card p-4">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold mb-2">{s.n}</span>
                    <p className="text-sm font-semibold">{s.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{s.body}</p>
                  </div>
                ))}
              </div>
              <Button onClick={() => navigate('/client/talentcompetency/evo')} className="gap-1.5" data-testid="tco-client-hero-cta">
                <Target className="h-4 w-4" /> {t('tco.client.hero.cta', { defaultValue: 'Configurar competencias' })}
              </Button>
              <Button variant="outline" onClick={() => setCaseBOpen(true)} className="gap-1.5 ml-2" data-testid="tco-client-hero-support-cta">
                <FilePlus className="h-4 w-4" /> {t('tco.client.requestNewProfile', { defaultValue: 'Solicitar apoyo de CTH' })}
              </Button>
            </div>
          </Card>
        )}

        {/* Founder UX Review — executive KPI row */}
        {!loading && (requests.length > 0 || profiles.length > 0) && (
          <div className="grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4" data-testid="tco-client-kpis">
            {[
              { id: 'pending', label: t('tco.client.kpi.pending', { defaultValue: 'Solicitudes pendientes' }), value: requests.filter((r) => r.status === 'pending').length, tone: 'text-amber-600' },
              { id: 'knowledge', label: t('tco.client.kpi.knowledge', { defaultValue: 'Evaluaciones de conocimientos' }), value: knowledge.length, tone: 'text-sky-600' },
              { id: 'active', label: t('tco.client.kpi.active', { defaultValue: 'Evaluaciones activas' }), value: requests.filter((r) => r.status === 'assigned').length, tone: 'text-emerald-600' },
              { id: 'profiles', label: t('tco.client.kpi.profiles', { defaultValue: 'Puestos configurados' }), value: profiles.length, tone: 'text-primary' },
              { id: 'total', label: t('tco.client.kpi.total', { defaultValue: 'Solicitudes totales' }), value: requests.length, tone: 'text-foreground' },
            ].map((k) => (
              <Card key={k.id} className="p-4 shadow-card" data-testid={`tco-client-kpi-${k.id}`}>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{k.label}</p>
                <p className={`text-2xl font-bold tabular-nums mt-1 ${k.tone}`}>{k.value}</p>
              </Card>
            ))}
          </div>
        )}

        {/* Founder UAT 2026-06 — executive analytics (real data, no line charts) */}
        {!loading && (knowledge.length > 0 || evaluations.length > 0 || catalog.length > 0 || companyComps.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="tco-analytics-row">
            <DonutCard
              title={t('tco.analytics.coverageTitle', { defaultValue: 'Cobertura de competencias' })}
              subtitle={t('tco.analytics.coverageSubtitle', { defaultValue: 'Competencias con y sin evaluación vinculada' })}
              testid="tco-chart-coverage"
              accent={SEM.teal}
              data={(() => {
                const codes = new Set([
                  ...catalog.map((c) => c.code || c.id),
                  ...companyComps.map((c) => c.id),
                ]);
                let linked = 0;
                codes.forEach((code) => { if ((compLinks[code] || []).length > 0) linked += 1; });
                return [
                  { name: t('tco.analytics.linked', { defaultValue: 'Con evaluación vinculada' }), value: linked, color: SEM.green },
                  { name: t('tco.analytics.unlinked', { defaultValue: 'Sin evaluación vinculada' }), value: Math.max(0, codes.size - linked), color: SEM.yellow },
                ];
              })()}
              emptyText={t('tco.analytics.noCatalog', { defaultValue: 'Aún no hay competencias en el catálogo.' })}
            />
            <DonutCard
              title={t('tco.analytics.statusTitle', { defaultValue: 'Estado de las evaluaciones' })}
              subtitle={t('tco.analytics.statusSubtitle', { defaultValue: 'Conocimientos y evaluaciones de puesto' })}
              testid="tco-chart-status"
              accent={SEM.blue}
              data={(() => {
                const all = [
                  ...knowledge.map((k) => k.stage),
                  ...evaluations.map((e) => (e.stage === 'assigned' ? 'assigned'
                    : e.stage === 'in_progress' ? 'in_progress' : 'completed')),
                ];
                return [
                  { name: t('tco.analytics.assigned', { defaultValue: 'Asignadas' }), value: all.filter((s) => s === 'assigned').length, color: SEM.yellow },
                  { name: t('tco.analytics.inProgress', { defaultValue: 'En curso' }), value: all.filter((s) => s === 'in_progress').length, color: SEM.orange },
                  { name: t('tco.analytics.completed', { defaultValue: 'Completadas' }), value: all.filter((s) => !['assigned', 'in_progress'].includes(s)).length, color: SEM.green },
                ];
              })()}
              emptyText={t('tco.analytics.noData', { defaultValue: 'Aún no hay evaluaciones registradas.' })}
            />
            <DonutCard
              title={t('tco.analytics.resultsTitle', { defaultValue: 'Resultados de conocimientos' })}
              subtitle={t('tco.analytics.resultsSubtitle', { defaultValue: 'Solo evaluaciones con umbral de aprobación' })}
              testid="tco-chart-results"
              accent={SEM.green}
              data={[
                { name: t('tco.analytics.passed', { defaultValue: 'Aprobadas' }), value: knowledge.filter((k) => k.passed === true).length, color: SEM.green },
                { name: t('tco.analytics.below', { defaultValue: 'Bajo el umbral' }), value: knowledge.filter((k) => k.passed === false).length, color: SEM.red },
                { name: t('tco.analytics.pendingResult', { defaultValue: 'Pendientes' }), value: knowledge.filter((k) => k.passed === null || k.passed === undefined).length, color: SEM.yellow },
              ]}
              emptyText={t('tco.analytics.noResults', { defaultValue: 'No hay evaluaciones de conocimientos todavía. Asigna la primera evaluación para comenzar a medir resultados.' })}
            />
            <BarsCard
              title={t('tco.analytics.originTitle', { defaultValue: 'Origen de las evaluaciones' })}
              subtitle={t('tco.analytics.originSubtitle', { defaultValue: 'Directas vs. post-capacitación (TalentTrain)' })}
              testid="tco-chart-origin"
              accent={SEM.orange}
              data={[
                { name: t('tco.analytics.direct', { defaultValue: 'Directas' }), value: knowledge.filter((k) => k.origin !== 'talenttrain').length, color: SEM.blue },
                { name: t('tco.analytics.postTraining', { defaultValue: 'Post-capacitación' }), value: knowledge.filter((k) => k.origin === 'talenttrain').length, color: SEM.orange },
                { name: t('tco.analytics.positionLinked', { defaultValue: 'Vinculadas al puesto' }), value: evaluations.length, color: SEM.teal },
              ]}
              emptyText={t('tco.analytics.noData', { defaultValue: 'Aún no hay evaluaciones registradas.' })}
            />
          </div>
        )}

        {/* Founder-approved enhancement 2026-06 — dept × competency coverage */}
        {!loading && <CompetencyCoverageHeatmap />}

        {/* V5.0 §12 — pending competency evaluations (operational follow-up) */}
        {!loading && <PendingGapsCard t={t} />}

        {/* Founder UAT 2026-06 — Knowledge assessments (no position required) */}
        {!loading && knowledge.length > 0 && (
          <Card className="p-5" data-testid="tco-knowledge-card">
            <div className="flex items-center gap-3 mb-1">
              <BookOpenCheck className="h-5 w-5 text-sky-600" />
              <h2 className="font-semibold">{t('tco.knowledge.title', { defaultValue: 'Evaluaciones de conocimientos' })}</h2>
              <Badge variant="outline" className="text-xs">{knowledge.length}</Badge>
              <span className="flex-1" />
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1"
                      onClick={() => exportKnowledge('pdf')} data-testid="tco-knowledge-export-pdf">
                <FileText className="h-3 w-3" /> PDF
              </Button>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1"
                      onClick={() => exportKnowledge('xlsx')} data-testid="tco-knowledge-export-xlsx">
                <FileText className="h-3 w-3" /> Excel
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {t('tco.knowledge.subtitle', { defaultValue: 'Evaluaciones asignadas directamente al colaborador — sin requerir un puesto configurado' })}
            </p>
            <ul className="divide-y" data-testid="tco-knowledge-list">
              {knowledge.map((k) => (
                <li key={k.assignment_id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`tco-knowledge-row-${k.assignment_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{k.employee_name}</span>
                      <Badge variant="outline" className={`text-xs ${
                        k.stage === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : k.stage === 'in_progress' ? 'bg-orange-50 text-orange-700 border-orange-200'
                            : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                      }`} data-testid={`tco-knowledge-stage-${k.assignment_id}`}>
                        {k.stage === 'completed'
                          ? t('tco.knowledge.stageCompleted', { defaultValue: 'Completada' })
                          : k.stage === 'in_progress'
                            ? t('tco.knowledge.stageInProgress', { defaultValue: 'En curso' })
                            : t('tco.knowledge.stageAssigned', { defaultValue: 'Disponible en Mi Portal' })}
                      </Badge>
                      {k.origin === 'talenttrain' && (
                        <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200" title={k.training_title || ''}>
                          <GraduationCap className="h-3 w-3 mr-1" />
                          TalentTrain{k.training_title ? ` · ${k.training_title}` : ''}
                        </Badge>
                      )}
                      {k.score != null && (
                        <Badge variant="outline" className={`text-xs tabular-nums ${
                          k.passed === true ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : k.passed === false ? 'bg-red-50 text-red-700 border-red-200'
                              : 'bg-sky-50 text-sky-700 border-sky-200'
                        }`} data-testid={`tco-knowledge-score-${k.assignment_id}`}>
                          {Math.round(k.score)}/100
                          {k.passed === true ? ` · ${t('tco.knowledge.passed', { defaultValue: 'Aprobada' })}` : ''}
                          {k.passed === false ? ` · ${t('tco.knowledge.failed', { defaultValue: 'Bajo el umbral' })}` : ''}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                      <span className="truncate max-w-[24rem]">{k.assessment_title}</span>
                      {k.created_at && <span>{t('tco.knowledge.assignedAt', { defaultValue: 'Asignada' })}: {new Date(k.created_at).toLocaleDateString(getUiLocale())}</span>}
                      {k.submitted_at && <span>{t('tco.knowledge.completedAt', { defaultValue: 'Completada' })}: {new Date(k.submitted_at).toLocaleDateString(getUiLocale())}</span>}
                    </div>
                  </div>
                  {k.stage !== 'completed' && (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1 shrink-0 self-start sm:self-center"
                            disabled={remindBusy === k.assignment_id}
                            onClick={() => sendReminder(k.assignment_id)}
                            data-testid={`tco-knowledge-remind-${k.assignment_id}`}>
                      {remindBusy === k.assignment_id
                        ? t('common.sending', { defaultValue: 'Enviando…' })
                        : t('tco.knowledge.remind', { defaultValue: 'Recordar' })}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* TC-Closeout FIX 16 — Evaluaciones de competencias (execution view) */}
        {!loading && evaluations.length > 0 && (
          <Card className="p-5" data-testid="tco-client-evaluations-card">
            <div className="flex items-center gap-3 mb-1">
              <UserRound className="h-5 w-5 text-muted-foreground" />
              <h2 className="font-semibold">{t('tco.client.evaluationsTitleV2', { defaultValue: 'Evaluaciones vinculadas al puesto' })}</h2>
              <Badge variant="outline" className="text-xs">{evaluations.length}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              {t('tco.client.evaluationsSubtitle', { defaultValue: 'Colaboradores evaluados contra un Perfil de Puesto y el estado de su informe' })}
            </p>
            <ul className="divide-y" data-testid="tco-client-evaluations-list">
              {evaluations.map((ev) => (
                <li key={ev.assignment_id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`tco-client-evaluation-row-${ev.assignment_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{ev.participant_name || employeeName(ev.employeeUserId)}</span>
                      <Badge variant="outline" className={`text-xs ${STAGE_TONES[ev.stage] || ''}`} data-testid={`tco-client-evaluation-stage-${ev.assignment_id}`}>
                        {t(`tco.stage.${ev.stage}`, { defaultValue: ev.stage })}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                      {ev.position && <span>{ev.position}</span>}
                      {ev.title && <span className="truncate max-w-[22rem]">{ev.title}</span>}
                      {ev.submitted_at && (
                        <span>{t('tco.stage.evaluation_completed', { defaultValue: 'Evaluación completada' })}: {new Date(ev.submitted_at).toLocaleDateString(getUiLocale())}</span>
                      )}
                    </div>
                  </div>
                  {ev.stage === 'report_published' && ev.report_id && (
                    <Button size="sm" variant="outline" className="gap-1.5 shrink-0"
                            onClick={() => navigate(`/client/reports/${ev.report_id}`)}
                            data-testid={`tco-client-evaluation-report-btn-${ev.assignment_id}`}>
                      <FileText className="h-4 w-4" /> {t('tco.client.viewReport', { defaultValue: 'Ver informe' })}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card className="p-5">
          <div className="flex items-center gap-3">
            <ClipboardList className="h-5 w-5 text-muted-foreground" />
            <h2 className="font-semibold">{t('tco.client.yourRequests', { defaultValue: 'Your requests' })}</h2>
            <Badge variant="outline" className="text-xs">{requests.length}</Badge>
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          {loading ? (
            <div className="p-8 text-sm text-muted-foreground" data-testid="tco-client-loading">
              {t('common.loading', { defaultValue: 'Loading…' })}
            </div>
          ) : requests.length === 0 ? (
            <div className="p-12 text-center" data-testid="tco-client-empty">
              <Target className="h-10 w-10 mx-auto text-muted-foreground/60 mb-3" />
              <p className="text-sm text-muted-foreground">
                {t('tco.client.noRequestsYet', {
                  defaultValue: 'No requests yet. Start one with the buttons above.',
                })}
              </p>
            </div>
          ) : (
            <ul className="divide-y" data-testid="tco-client-request-list">
              {requests.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setDetailId(r.id)}
                    className="w-full text-left px-5 py-4 hover:bg-accent/40 transition flex items-start gap-3"
                    data-testid={`tco-client-request-row-${r.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate max-w-[26rem]" title={r.type === 'existing_profile' ? profileName(r.competency_profile_id) : (r.proposed_title || '')}>
                          {r.type === 'existing_profile'
                            ? (profileName(r.competency_profile_id))
                            : (r.proposed_title || t('tco.client.untitled', { defaultValue: '(New profile request)' }))}
                        </span>
                        <Badge variant="outline" className={`text-xs ${STATUS_TONES[r.status] || ''}`} data-testid={`tco-client-request-status-${r.id}`}>
                          {t(`tco.client.status.${r.status}`, { defaultValue: r.status })}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {t(`tco.client.type.${r.type}`, { defaultValue: r.type })}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                        {r.employee_user_id && (
                          <span>{t('tco.client.employee', { defaultValue: 'Employee' })}: {employeeName(r.employee_user_id)}</span>
                        )}
                        {r.position && <span>{t('tco.client.positionLabel', { defaultValue: 'Position' })}: {r.position}</span>}
                        <span>{t('tco.client.requestedAt', { defaultValue: 'Requested' })}: {new Date(r.requested_at).toLocaleString(getUiLocale())}</span>
                      </div>
                    </div>
                    <Eye className="h-4 w-4 text-muted-foreground self-center" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Evaluar colaborador — Founder UAT 2026-06: knowledge vs position */}
      <Dialog open={caseAOpen} onOpenChange={(o) => { if (!o) closeCaseA(); }}>
        <DialogContent className="max-w-lg lg:max-w-2xl" data-testid="tco-client-case-a-dialog">
          <DialogHeader>
            <DialogTitle>{t('tco.client.caseATitleV2', { defaultValue: 'Evaluar colaborador' })}</DialogTitle>
            <DialogDescription>
              {t('tco.client.caseADescV3', { defaultValue: 'Elige al colaborador y el propósito de la evaluación. Las evaluaciones de conocimientos no requieren un puesto configurado.' })}
            </DialogDescription>
          </DialogHeader>
          {handoffCtx && (
            <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800 flex items-center gap-2" data-testid="tco-handoff-banner">
              <GraduationCap className="h-4 w-4 shrink-0" />
              <span>
                {t('tco.client.handoffOrigin', { defaultValue: 'Origen: TalentTrain' })}
                {handoffCtx.trainingTitle ? ` · ${t('tco.client.handoffTraining', { defaultValue: 'Capacitación' })}: ${handoffCtx.trainingTitle}` : ''}
              </span>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label htmlFor="tco-case-a-employee">{t('tco.client.employee', { defaultValue: 'Employee' })} <span className="text-destructive">*</span></Label>
              <Select
                value={caseADraft.employee_user_id}
                onValueChange={(v) => setCaseADraft((d) => ({ ...d, employee_user_id: v }))}
              >
                <SelectTrigger id="tco-case-a-employee" data-testid="tco-client-case-a-employee">
                  <SelectValue placeholder={t('tco.client.pickEmployee', { defaultValue: 'Pick an employee…' })} />
                </SelectTrigger>
                <SelectContent>
                  {employees.length === 0 ? (
                    <SelectItem disabled value="__none__">{t('tco.client.noEmployees', { defaultValue: 'No employees available' })}</SelectItem>
                  ) : employees.map((e) => (
                    <SelectItem key={e.id} value={e.id} data-testid={`tco-client-case-a-employee-opt-${e.id}`}>
                      {e.name} — {e.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Purpose choice — visibly distinct workflows */}
            <div>
              <Label>{t('tco.client.purposeLabel', { defaultValue: 'Propósito de la evaluación' })} <span className="text-destructive">*</span></Label>
              <div className="grid grid-cols-2 gap-2 mt-1" data-testid="tco-purpose-choice">
                <button type="button" onClick={() => setPurpose('knowledge')}
                        data-testid="tco-purpose-knowledge"
                        className={`rounded-lg border p-3 text-left transition-colors ${purpose === 'knowledge' ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-300' : 'border-border hover:bg-muted/50'}`}>
                  <span className="flex items-center gap-1.5 text-sm font-semibold">
                    <BookOpenCheck className="h-4 w-4 text-sky-600" />
                    {t('tco.client.purposeKnowledge', { defaultValue: 'Evaluación de conocimientos' })}
                  </span>
                  <span className="text-xs text-muted-foreground block mt-1">
                    {t('tco.client.purposeKnowledgeDesc', { defaultValue: 'Directa o post-capacitación. No requiere puesto configurado.' })}
                  </span>
                </button>
                <button type="button" onClick={() => setPurpose('position')}
                        data-testid="tco-purpose-position"
                        className={`rounded-lg border p-3 text-left transition-colors ${purpose === 'position' ? 'border-primary bg-primary/5 ring-1 ring-primary/40' : 'border-border hover:bg-muted/50'}`}>
                  <span className="flex items-center gap-1.5 text-sm font-semibold">
                    <Target className="h-4 w-4 text-primary" />
                    {t('tco.client.purposePosition', { defaultValue: 'Evaluación vinculada al puesto' })}
                  </span>
                  <span className="text-xs text-muted-foreground block mt-1">
                    {t('tco.client.purposePositionDesc', { defaultValue: 'Contra las competencias configuradas del puesto.' })}
                  </span>
                </button>
              </div>
            </div>

            {purpose === 'knowledge' ? (
              <>
                <div>
                  <Label>{t('tco.client.assessmentLabel', { defaultValue: 'Evaluación (Biblioteca de evaluaciones)' })} <span className="text-destructive">*</span></Label>
                  {/* Founder UAT 2026-06 — searchable, deduplicated, bounded selector */}
                  <Popover open={assessOpen} onOpenChange={setAssessOpen} modal>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" aria-expanded={assessOpen}
                              className="w-full justify-between font-normal h-auto min-h-9 py-2"
                              data-testid="tco-knowledge-assessment">
                        <span className="truncate text-left">
                          {knowledgeDraft.questionnaire_id
                            ? (selectorOptions.find((o) => o.id === knowledgeDraft.questionnaire_id)?.label
                              || t('tco.client.pickAssessment', { defaultValue: 'Selecciona una evaluación publicada…' }))
                            : t('tco.client.pickAssessment', { defaultValue: 'Selecciona una evaluación publicada…' })}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" side="bottom" align="start" avoidCollisions={false}>
                      <Command>
                        <CommandInput placeholder={t('tco.client.searchAssessment', { defaultValue: 'Buscar evaluación…' })} data-testid="tco-assessment-search" />
                        <CommandList className="max-h-[min(48vh,380px)] overflow-auto">
                          <CommandEmpty>{t('tco.client.noLibAssessments', { defaultValue: 'No hay evaluaciones publicadas disponibles en tu biblioteca.' })}</CommandEmpty>
                          <CommandGroup>
                            {selectorOptions.map((o) => (
                              <CommandItem key={o.id} value={o.label}
                                           onSelect={() => { setKnowledgeDraft((d) => ({ ...d, questionnaire_id: o.id })); setAssessOpen(false); }}
                                           className="py-2.5"
                                           data-testid={`tco-knowledge-assessment-opt-${o.id}`}>
                                <Check className={`mr-2 h-4 w-4 shrink-0 ${knowledgeDraft.questionnaire_id === o.id ? 'opacity-100 text-sky-600' : 'opacity-0'}`} />
                                <span className="min-w-0">
                                  <span className="block font-medium leading-snug whitespace-normal break-words">{o.label}</span>
                                  <span className="block text-xs text-muted-foreground mt-0.5">
                                    {[o.area, t('tco.client.published', { defaultValue: 'Publicada' })].filter(Boolean).join(' · ')}
                                  </span>
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                {knowledgeDraft.questionnaire_id && (() => {
                  const qq = libAssessments.find((x) => x.id === knowledgeDraft.questionnaire_id);
                  if (!qq) return null;
                  return (
                    <div className="rounded-md border border-sky-200 bg-sky-50/50 px-3 py-2 text-xs" data-testid="tco-knowledge-meta">
                      <span className="font-semibold">{qq.name_es || qq.name || qq.title}</span>
                      {qq.professional_area ? <span className="text-muted-foreground"> · {qq.professional_area}</span> : null}
                      {qq.passing_score != null && (
                        <span className="block mt-0.5 text-muted-foreground">
                          {t('tco.client.passingScore', { defaultValue: 'Umbral de aprobación' })}: {qq.passing_score}/100
                        </span>
                      )}
                    </div>
                  );
                })()}
                <div>
                  <Label htmlFor="tco-knowledge-notes">{t('tco.client.notes', { defaultValue: 'Notes (optional)' })}</Label>
                  <Textarea
                    id="tco-knowledge-notes"
                    rows={2}
                    value={knowledgeDraft.notes}
                    onChange={(e) => setKnowledgeDraft((d) => ({ ...d, notes: e.target.value }))}
                    data-testid="tco-knowledge-notes"
                  />
                </div>
                {libAssessments.length === 0 && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="tco-no-assessment-hint">
                    {t('tco.client.noAssessmentHint', { defaultValue: '¿No existe una evaluación apropiada? Solicita al equipo CTH un nuevo instrumento desde la Biblioteca de evaluaciones, o usa "Solicitar apoyo de CTH".' })}
                  </p>
                )}
              </>
            ) : (
            <>
            {/* Founder UAT remediation 2026-06 — the canonical chain is
                resolved AUTOMATICALLY: collaborator → formal position →
                Role Profile → competencies → linked instruments. Manual
                profile selection appears ONLY without a formal position. */}
            {!caseADraft.employee_user_id ? (
              <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground" data-testid="tco-position-pick-employee-first">
                {t('tco.client.pickEmployeeFirst', { defaultValue: 'Selecciona primero al colaborador — CTH identificará automáticamente su puesto y las competencias configuradas.' })}
              </div>
            ) : resolving ? (
              <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground" data-testid="tco-position-resolving">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('tco.client.resolvingPosition', { defaultValue: 'Identificando el puesto del colaborador…' })}
              </div>
            ) : resolution?.status === 'resolved' ? (
              <>
                <div className="rounded-md border border-primary/25 bg-primary/5 px-3 py-2.5 text-xs" data-testid="tco-resolved-position">
                  <span className="text-muted-foreground">{t('tco.client.resolvedPositionLabel', { defaultValue: 'Puesto identificado automáticamente:' })}</span>{' '}
                  <span className="font-semibold">{resolution.position?.title}</span>
                  {resolution.role_profile?.department ? <span className="text-muted-foreground"> · {resolution.role_profile.department}</span> : null}
                </div>
                <div>
                  <Label>{t('tco.client.selectCompetencies', { defaultValue: 'Competencias configuradas del puesto — selecciona cuáles evaluar' })} <span className="text-destructive">*</span></Label>
                  <div className="mt-1 space-y-1.5 max-h-52 overflow-y-auto pr-1" data-testid="tco-resolved-competencies">
                    {(resolution.competencies || []).map((c) => (
                      <div key={c.code} className={`flex items-start gap-2.5 rounded-md border px-2.5 py-2 ${selectedCodes.includes(c.code) ? 'border-primary/50 bg-primary/5' : 'border-border'}`}>
                        <Checkbox
                          id={`tco-comp-${c.code}`}
                          checked={selectedCodes.includes(c.code)}
                          disabled={!c.has_instrument}
                          onCheckedChange={(v) => setSelectedCodes((prev) => (v ? [...prev, c.code] : prev.filter((x) => x !== c.code)))}
                          data-testid={`tco-comp-check-${c.code}`}
                          className="mt-0.5"
                        />
                        <label htmlFor={`tco-comp-${c.code}`} className="flex-1 min-w-0 cursor-pointer">
                          <span className="block text-sm font-medium truncate" title={(i18n.language || '').startsWith('es') ? (c.name_es || c.name) : (c.name_en || c.name)}>
                            {(i18n.language || '').startsWith('es') ? (c.name_es || c.name) : (c.name_en || c.name)}
                          </span>
                          {c.expected_level != null && (
                            <span className="block text-xs text-muted-foreground">
                              {t('tco.client.expectedLevel', { defaultValue: 'Nivel esperado' })}: {c.expected_level}
                            </span>
                          )}
                          {!c.has_instrument && (c.instruments || []).length > 0 && (
                            <span className="block text-[11px] text-amber-700" data-testid={`tco-comp-reason-${c.code}`}>
                              {{
                                not_published: t('tco.client.reason.notPublished', { defaultValue: 'La evaluación vinculada no está publicada.' }),
                                not_available_for_company: t('tco.client.reason.notAvailable', { defaultValue: 'La evaluación vinculada no está disponible para uso de empresas.' }),
                                suite_not_contracted: t('tco.client.reason.suite', { defaultValue: 'La evaluación pertenece a una suite no contratada.' }),
                                no_active_configuration: t('tco.client.reason.noConfig', { defaultValue: 'La evaluación vinculada no tiene una configuración activa.' }),
                              }[(c.instruments.find((x) => !x.assignable) || {}).reason] || t('tco.client.reason.generic', { defaultValue: 'La evaluación vinculada no está disponible para asignación.' })}
                            </span>
                          )}
                        </label>
                        {c.has_instrument ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] shrink-0" data-testid={`tco-comp-linked-${c.code}`}>
                            ✓ {t('tco.client.instrumentLinked', { defaultValue: 'Instrumento vinculado' })}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] shrink-0" data-testid={`tco-comp-unlinked-${c.code}`}>
                            {t('tco.client.noInstrument', { defaultValue: 'Sin instrumento' })}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                  {(resolution.competencies || []).some((c) => !c.has_instrument) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="tco-missing-instrument-actions">
                      <span className="text-amber-700">{t('tco.client.missingInstrumentHint', { defaultValue: 'Para las competencias sin instrumento:' })}</span>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs gap-1"
                              onClick={() => {
                                const missing = (resolution.competencies || []).filter((x) => !x.has_instrument).map((x) => x.code);
                                navigate(`/client/talentcompetency/competencias?link=${encodeURIComponent(missing[0] || '')}&queue=${encodeURIComponent(missing.join(','))}&return=evaluar`);
                              }} data-testid="tco-link-assessment-btn">
                        <Link2 className="h-3 w-3" /> {t('tco.client.linkAssessment', { defaultValue: 'Vincular evaluación' })}
                      </Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs gap-1" onClick={openPublicationRequest} data-testid="tco-request-cth-btn">
                        <LifeBuoy className="h-3 w-3" /> {t('tco.client.requestCth', { defaultValue: 'Solicitar apoyo de CTH' })}
                      </Button>
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="tco-case-a-notes">{t('tco.client.notes', { defaultValue: 'Notes (optional)' })}</Label>
                  <Textarea
                    id="tco-case-a-notes"
                    rows={2}
                    value={caseADraft.notes}
                    onChange={(e) => setCaseADraft((d) => ({ ...d, notes: e.target.value }))}
                    data-testid="tco-client-case-a-notes"
                  />
                </div>
              </>
            ) : (resolution?.status === 'no_role_profile' || resolution?.status === 'no_competencies') ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 space-y-2" data-testid="tco-position-diagnosis">
                <p>
                  {resolution.status === 'no_role_profile'
                    ? t('tco.client.diag.noProfileV2', { defaultValue: 'El puesto del colaborador ("{{pos}}") aún no tiene un Perfil de Puesto con competencias configuradas. Configúralo directamente o solicita apoyo de CTH.', pos: resolution.position?.title })
                    : t('tco.client.diag.noCompetenciesV2', { defaultValue: 'El Perfil de Puesto de "{{pos}}" todavía no tiene competencias configuradas.', pos: resolution.position?.title })}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => navigate('/client/talentcompetency/competencias')} data-testid="tco-diag-configure-btn">
                    {t('tco.client.configureCompetencies', { defaultValue: 'Configurar competencias' })}
                  </Button>
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={openPublicationRequest} data-testid="tco-diag-request-cth-btn">
                    {t('tco.client.requestCth', { defaultValue: 'Solicitar apoyo de CTH' })}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800" data-testid="tco-no-position-explainer">
                  {t('tco.client.diag.noPositionV2', { defaultValue: 'Este colaborador no tiene un puesto formal vinculado en la estructura organizacional; por eso debes seleccionar manualmente el perfil de puesto a evaluar. Puedes vincular su puesto desde Departamentos → Gestionar puestos.' })}
                </div>
                <div>
                  <Label htmlFor="tco-case-a-profile">{t('tco.client.manualProfile', { defaultValue: 'Perfil de puesto (selección manual)' })} <span className="text-destructive">*</span></Label>
                  <Select
                    value={caseADraft.competency_profile_id}
                    onValueChange={(v) => setCaseADraft((d) => ({ ...d, competency_profile_id: v }))}
                  >
                    <SelectTrigger id="tco-case-a-profile" data-testid="tco-client-case-a-profile">
                      <SelectValue placeholder={t('tco.client.pickProfileV2', { defaultValue: 'Selecciona un perfil de puesto publicado…' })} />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        // Founder P0 2026-09 — the selector only offers what the
                        // backend will actually accept (deployable configurations).
                        const assignable = profiles.filter((p) => p.deployable !== false);
                        return assignable.length === 0 ? (
                          <SelectItem disabled value="__none__">{t('tco.client.noProfiles', { defaultValue: 'Aún no hay competencias de puesto listas para evaluar. Configúralas o solicita apoyo de CTH.' })}</SelectItem>
                        ) : assignable.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`tco-client-case-a-profile-opt-${p.id}`}>
                          <span className="block max-w-[380px] truncate" title={`${p.name}${p.department ? ` · ${p.department}` : ''}`}>
                            {p.name}{p.department ? ` · ${p.department}` : ''}
                          </span>
                        </SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                {/* Founder UAT TC-013/014 — read-only target derived from the profile */}
                {caseADraft.competency_profile_id && (() => {
                  const p = profiles.find((x) => x.id === caseADraft.competency_profile_id);
                  if (!p) return null;
                  return (
                    <div className="rounded-md border border-primary/25 bg-primary/5 px-3 py-2 text-xs" data-testid="tco-client-case-a-target">
                      <span className="text-muted-foreground">{t('tco.client.evaluateAgainst', { defaultValue: 'Will be evaluated against:' })}</span>{' '}
                      <span className="font-semibold" title={p.name}>{p.name}</span>
                      {p.department ? <span className="text-muted-foreground"> · {p.department}</span> : null}
                    </div>
                  );
                })()}
                <div>
                  <Label htmlFor="tco-case-a-notes">{t('tco.client.notes', { defaultValue: 'Notes (optional)' })}</Label>
                  <Textarea
                    id="tco-case-a-notes"
                    rows={2}
                    value={caseADraft.notes}
                    onChange={(e) => setCaseADraft((d) => ({ ...d, notes: e.target.value }))}
                    data-testid="tco-client-case-a-notes"
                  />
                </div>
              </>
            )}
            </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeCaseA} data-testid="tco-client-case-a-cancel">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              onClick={purpose === 'knowledge'
                ? submitKnowledge
                : resolution?.status === 'resolved' ? submitPositionResolved : submitCaseA}
              disabled={submitting || (purpose === 'position' && (
                resolving
                || (resolution?.status === 'resolved' && selectedCodes.length === 0)
                || ['no_role_profile', 'no_competencies'].includes(resolution?.status)
              ))}
              data-testid="tco-client-case-a-submit"
            >
              {submitting
                ? t('common.sending', { defaultValue: 'Sending…' })
                : purpose === 'knowledge'
                  ? t('tco.client.assignKnowledge', { defaultValue: 'Asignar evaluación' })
                  : t('tco.client.assignEvaluation', { defaultValue: 'Evaluar colaborador' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Case B dialog */}
      <Dialog open={caseBOpen} onOpenChange={setCaseBOpen}>
        <DialogContent className="max-w-lg" data-testid="tco-client-case-b-dialog">
          <DialogHeader>
            <DialogTitle>{t('tco.client.caseBTitle', { defaultValue: 'Solicitar apoyo de CTH' })}</DialogTitle>
            <DialogDescription>
              {t('tco.client.caseBDesc', { defaultValue: 'Describe el puesto y el equipo CTH te apoyará definiendo las competencias del puesto. También puedes configurarlas directamente desde "Configurar competencias".' })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="tco-case-b-title">{t('tco.client.proposedTitle', { defaultValue: 'Position title' })} <span className="text-destructive">*</span></Label>
              <Input
                id="tco-case-b-title"
                value={caseBDraft.proposed_title}
                onChange={(e) => setCaseBDraft((d) => ({ ...d, proposed_title: e.target.value }))}
                placeholder="e.g. Chemical Engineer"
                data-testid="tco-client-case-b-title"
              />
            </div>
            <div>
              <Label htmlFor="tco-case-b-dept">{t('tco.client.proposedDepartment', { defaultValue: 'Department' })}</Label>
              <Input
                id="tco-case-b-dept"
                value={caseBDraft.proposed_department}
                onChange={(e) => setCaseBDraft((d) => ({ ...d, proposed_department: e.target.value }))}
                placeholder="e.g. Operations"
                data-testid="tco-client-case-b-dept"
              />
            </div>
            <div>
              <Label htmlFor="tco-case-b-desc">{t('tco.client.proposedDescription', { defaultValue: 'Short description' })}</Label>
              <Textarea
                id="tco-case-b-desc"
                rows={2}
                value={caseBDraft.proposed_description}
                onChange={(e) => setCaseBDraft((d) => ({ ...d, proposed_description: e.target.value }))}
                data-testid="tco-client-case-b-desc"
              />
            </div>
            <div>
              <Label htmlFor="tco-case-b-just">{t('tco.client.justification', { defaultValue: 'Business justification' })} <span className="text-destructive">*</span></Label>
              <Textarea
                id="tco-case-b-just"
                rows={3}
                value={caseBDraft.business_justification}
                onChange={(e) => setCaseBDraft((d) => ({ ...d, business_justification: e.target.value }))}
                placeholder={t('tco.client.justificationPh', { defaultValue: 'Why this role matters now, headcount, urgency…' })}
                data-testid="tco-client-case-b-just"
              />
            </div>
            <div>
              <Label htmlFor="tco-case-b-employee">{t('tco.client.employeeOptional', { defaultValue: 'Employee to evaluate (optional)' })}</Label>
              <Select
                value={caseBDraft.employee_user_id}
                onValueChange={(v) => setCaseBDraft((d) => ({ ...d, employee_user_id: v }))}
              >
                <SelectTrigger id="tco-case-b-employee" data-testid="tco-client-case-b-employee">
                  <SelectValue placeholder={t('tco.client.skipEmployee', { defaultValue: 'Omitir por ahora (opcional)' })} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id} data-testid={`tco-client-case-b-employee-opt-${e.id}`}>
                      {e.name} — {e.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCaseBOpen(false)} data-testid="tco-client-case-b-cancel">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={submitCaseB} disabled={submitting} data-testid="tco-client-case-b-submit">
              {submitting ? t('common.sending', { defaultValue: 'Sending…' }) : t('tco.client.send', { defaultValue: 'Enviar solicitud de apoyo' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail sheet */}
      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) setDetailId(null); }}>
        <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="tco-client-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle>
                  {detail.type === 'existing_profile'
                    ? profileName(detail.competency_profile_id)
                    : (detail.proposed_title || t('tco.client.untitled', { defaultValue: '(New profile request)' }))}
                </SheetTitle>
                <SheetDescription>
                  {t(`tco.client.type.${detail.type}`, { defaultValue: detail.type })}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-2 flex items-center gap-2">
                <Badge variant="outline" className={`text-xs ${STATUS_TONES[detail.status] || ''}`}>
                  {t(`tco.client.status.${detail.status}`, { defaultValue: detail.status })}
                </Badge>
              </div>

              <div className="mt-4 space-y-4 text-sm" data-testid={`tco-client-detail-${detail.id}`}>
                {detail.employee_user_id && (
                  <Row label={t('tco.client.employee', { defaultValue: 'Employee' })}>
                    {employeeName(detail.employee_user_id)}
                  </Row>
                )}
                {detail.position && (
                  <Row label={t('tco.client.positionLabel', { defaultValue: 'Position' })}>{detail.position}</Row>
                )}
                {detail.type === 'new_profile' && (
                  <>
                    {detail.proposed_department && <Row label={t('tco.client.proposedDepartment', { defaultValue: 'Department' })}>{detail.proposed_department}</Row>}
                    {detail.proposed_description && <Row label={t('tco.client.proposedDescription', { defaultValue: 'Description' })}>{detail.proposed_description}</Row>}
                    {detail.business_justification && <Row label={t('tco.client.justification', { defaultValue: 'Business justification' })}>{detail.business_justification}</Row>}
                  </>
                )}
                {detail.notes && <Row label={t('tco.client.notes', { defaultValue: 'Notes' })}>{detail.notes}</Row>}

                {detail.status === 'profile_available' && (
                  <div className="border rounded-md p-3 bg-sky-50/40 text-sky-900" data-testid="tco-client-detail-available-card">
                    <div className="flex items-center gap-2 font-medium">
                      <Sparkles className="h-4 w-4" />
                      {t('tco.client.availableHeader', { defaultValue: '✔ Competencias del puesto disponibles' })}
                    </div>
                    <p className="text-xs mt-1">
                      {t('tco.client.availableBody', {
                        defaultValue: 'El perfil que solicitaste (“{{name}}”) ya está publicado. Ahora puedes solicitar evaluaciones de colaboradores usando este perfil desde “Nueva solicitud” (perfil existente).',
                        name: detail.fulfilled_profile_name || detail.proposed_title || '',
                      })}
                    </p>
                  </div>
                )}

                {detail.status === 'assigned' && (
                  <div className="border rounded-md p-3 bg-emerald-50/40 text-emerald-900" data-testid="tco-client-detail-assigned-card">
                    <div className="flex items-center gap-2 font-medium">
                      <Sparkles className="h-4 w-4" />
                      {t('tco.client.assignedHeader', { defaultValue: 'Assignments created' })}
                    </div>
                    <p className="text-xs mt-1">
                      {t('tco.client.assignedBody', {
                        defaultValue: '{{n}} assessment(s) sent to the employee.',
                        n: (detail.assigned_assignment_ids || []).length,
                      })}
                    </p>
                  </div>
                )}

                {/* TC-Closeout FIX 6 — downstream execution lifecycle stepper */}
                {detail.execution && (
                  <div className="border rounded-md p-3" data-testid="tco-client-detail-execution">
                    <p className="font-medium text-sm mb-2">{t('tco.client.executionTitle', { defaultValue: 'Progreso de la evaluación' })}</p>
                    {detail.execution.assignments.map((ev) => {
                      const reachedIdx = EXECUTION_STAGES.indexOf(ev.stage);
                      return (
                        <div key={ev.assignment_id} className="mb-3 last:mb-0" data-testid={`tco-client-exec-${ev.assignment_id}`}>
                          <p className="text-xs font-medium mb-1.5">
                            {ev.participant_name || employeeName(detail.employee_user_id)}
                            {ev.position ? <span className="text-muted-foreground"> · {ev.position}</span> : null}
                          </p>
                          <ol className="space-y-1">
                            {EXECUTION_STAGES.map((s, i) => (
                              <li key={s} className="flex items-center gap-2 text-xs">
                                <span className={`h-2 w-2 rounded-full shrink-0 ${i <= reachedIdx ? 'bg-emerald-500' : 'bg-zinc-200'}`} />
                                <span className={i <= reachedIdx ? 'text-foreground' : 'text-muted-foreground'}>
                                  {t(`tco.stage.${s}`, { defaultValue: s })}
                                </span>
                                {i === reachedIdx && (
                                  <Badge variant="outline" className={`text-xs ${STAGE_TONES[s] || ''}`}>●</Badge>
                                )}
                              </li>
                            ))}
                          </ol>
                          {ev.stage === 'report_published' && ev.report_id && (
                            <Button size="sm" variant="outline" className="gap-1.5 mt-2"
                                    onClick={() => navigate(`/client/reports/${ev.report_id}`)}
                                    data-testid={`tco-client-exec-report-btn-${ev.assignment_id}`}>
                              <FileText className="h-4 w-4" /> {t('tco.client.viewReport', { defaultValue: 'Ver informe' })}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {detail.status === 'rejected' && detail.rejected_reason && (
                  <Row label={t('tco.client.rejectedReason', { defaultValue: 'Rejection reason' })}>{detail.rejected_reason}</Row>
                )}

                {detail.status === 'pending' && (
                  <Button variant="outline" onClick={cancelRequest} disabled={cancelling} data-testid="tco-client-detail-cancel-btn">
                    <X className="h-4 w-4 mr-1.5" />
                    {cancelling ? t('common.working', { defaultValue: 'Working…' }) : t('tco.client.cancel', { defaultValue: 'Cancel request' })}
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="col-span-2">{children}</span>
    </div>
  );
}
